<template>
  <div
    class="border-static-default-low mb-56 grid items-center md:mb-0 md:min-h-64 md:gap-y-0 md:border-b"
    :class="gridClasses"
  >
    <!-- Grade -->
    <div :class="rowClasses">
      <p :class="headingClasses">{{ i18n(translations.listingGradeLabel) }}</p>
      <RevTooltip class="md:w-full" position="top-start">
        <template #trigger="{ show, hide }">
          <RevTag
            class="cursor-pointer"
            data-test="grade-name"
            :label="getQuoteGradeName(listing.grade)"
            :variant="tagVariant"
            @focusin="show"
            @focusout="hide"
            @mouseleave="hide"
            @mouseover="show"
          />
        </template>
        {{ getQuoteGradeName(listing.grade) }}
      </RevTooltip>
    </div>
    <!-- Price to win BackBox  -->
    <div :class="rowClasses">
      <div class="body-1-bold flex items-center" :class="headingClasses">
        <p class="text-balance">
          {{ i18n(translations.priceToWinBackboxTitle) }}
        </p>
        <RevTooltip position="bottom-end">
          <template #trigger="{ show, hide }">
            <RevButtonIcon
              class="cursor-pointer"
              :disabled="false"
              :icon="IconInfo"
              variant="secondary"
              @focusin="show"
              @focusout="hide"
              @mouseleave="hide"
              @mouseover="show"
            />
          </template>
          {{ i18n(translations.priceToWinBackboxTooltip) }}
        </RevTooltip>
      </div>
      <RevSpinner v-if="isPriceToWinLoading" class="mx-auto"></RevSpinner>
      <p v-else>
        {{
          i18n.price(priceToWinBackbox, {
            trailingZeroDisplay: 'stripIfInteger',
          })
        }}
      </p>
    </div>
    <!-- BackBox price -->
    <div :class="rowClasses">
      <p :class="headingClasses">
        {{ i18n(translations.listingBackBoxPriceLabel) }}
      </p>
      <p>
        {{
          i18n.price(backboxPrice, {
            trailingZeroDisplay: 'stripIfInteger',
          })
        }}
      </p>
    </div>
    <!-- Price -->
    <div :class="rowClasses">
      <p :class="headingClasses">
        {{ i18n(translations.listingPriceLabel) }}
      </p>
      <RevInputText
        :id="`${index}-price`"
        v-model="formattedPrice"
        class="w-[7.5rem] md:w-auto"
        :format="(value) => removeNonDecimalCharacters(value as string)"
        :has-clear-button="false"
        :label="i18n(translations.priceInputLabel, { currency })"
      />
    </div>
    <!-- Status -->
    <div :class="rowClasses">
      <p :class="headingClasses">{{ i18n(translations.listingStatusLabel) }}</p>
      <p :class="statusMessage.classes">
        <component
          :is="statusMessage.component"
          v-if="statusMessage?.component"
          class="mr-8 hidden shrink-0 md:block"
        />
        {{ statusMessage.message }}
      </p>
    </div>
    <!-- Online/Offline/Save buttons -->
    <div :class="rowClasses">
      <p :class="headingClasses"></p>
      <div class="relative w-full justify-end">
        <div
          class="pointer-events-none absolute inset-0 z-10 grid place-content-center"
          :class="isLoading ? 'opacity-100' : 'opacity-0'"
        >
          <RevSpinner size="small" />
        </div>
        <div
          class="flex items-center justify-end gap-16 md:flex"
          :class="isLoading ? 'opacity-0' : 'opacity-100'"
        >
          <RevButtonBase
            class="text-static-default-hi body-1 cursor-pointer bg-transparent underline"
            :disabled="isTogglePublicationStateDisabled"
            @click="toggleState"
          >
            {{ publicationStateCtaLabel }}
          </RevButtonBase>
          <RevButton
            class="h-fit"
            :disabled="isSaveListingDisabled"
            size="small"
            variant="secondary"
            @click="savePrice"
          >
            {{ i18n(translations.save) }}
          </RevButton>
          <RevButton
            v-if="
              !listing.hasBackbox &&
              listing.state === LISTING_PUBLICATION_STATE.ONLINE
            "
            class="h-fit"
            size="small"
            variant="primary"
            @click="winBackbox"
          >
            {{ i18n(translations.winBackbox) }}
          </RevButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { getMarketplace } from '@backmarket/nuxt-module-marketplace/getMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { tw } from '@backmarket/utils/string/tw'
import { RevButton } from '@ds/components/Button'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevInputText } from '@ds/components/InputText'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag, type Variants } from '@ds/components/Tag'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconWarning } from '@ds/icons/IconWarning'

import {
  type BuyBackQuoteListing,
  BuyBackQuoteListingStates,
  type BuyBackQuotePricingEstimation,
  LISTING_PUBLICATION_STATE,
  type PostBuyBackQuoteListing,
} from '~/api/quotes/types'
import { usePriceToWinBackbox } from '~/scopes/buyback/composables/usePriceToWinBackbox'
import { getQuoteGradeName } from '~/scopes/buyback/pages/wishlist/WishList.utils'
import {
  removeDecimalsFromString,
  removeNonDecimalCharacters,
} from '~/scopes/buyback/utils/buyback.utils'

import translations from './ListingLine.translations'

const props = withDefaults(
  defineProps<{
    listing: BuyBackQuoteListing
    index: number
    isLoading: boolean
    marketCode: string
    tagVariant: Variants
    isPriceToWinLoading?: boolean
    pricingEstimation?: BuyBackQuotePricingEstimation
    gridClasses: string
    title: string
  }>(),
  {
    isPriceToWinLoading: false,
    pricingEstimation: undefined,
  },
)

const emit = defineEmits<{
  (e: 'save-price', payload: PostBuyBackQuoteListing): void
  (e: 'toggle-state', payload: PostBuyBackQuoteListing): void
}>()

const i18n = useI18n()
const { trackClick } = useTracking()

const formattedPrice = ref(
  removeDecimalsFromString(props.listing.price?.amount),
)
const initialPrice = ref(formattedPrice.value)

const rowClasses = tw`min-h-96 flex gap-x-16 items-center justify-between border-static-default-low border-b md:border-0 md:gap-x-0`
const headingClasses = tw`body-1-bold md:hidden`

const currency = computed(() => {
  if (props.listing.price?.currency) {
    return props.listing.price?.currency
  }

  return getMarketplace(props.marketCode as MarketCountryCode)?.market
    ?.defaultCurrency
})

const { pricingEstimation, listing } = toRefs(props)
const priceToWinBackbox = usePriceToWinBackbox(
  pricingEstimation,
  listing,
  currency,
)

const backboxPrice = computed(() =>
  !props.listing.backboxPrice
    ? {
        amount: '0',
        currency: currency.value,
      }
    : props.listing.backboxPrice,
)

const isListingOnline = computed(
  () => props.listing.state === LISTING_PUBLICATION_STATE.ONLINE,
)

// TODO: Add statusMessage type
const statusMessage = computed(() => {
  const common = tw`body-1-light flex items-center text-start py-8`
  const success = tw`text-static-success-hi`
  const danger = tw`text-static-danger-hi`
  const offline = tw`text-static-default-hi`

  if (!isListingOnline.value) {
    return {
      component: null,
      message: i18n(translations.offlineStatus),
      classes: [common, offline],
    }
  }

  if (props.listing.hasBackbox) {
    return {
      component: IconCheckInCircle,
      message: i18n(translations.backboxPriceOkMessage),
      classes: [common, success],
    }
  }

  return {
    component: IconWarning,
    message: i18n(translations.backboxPriceWarningMessage, {
      backboxPrice: i18n.price(priceToWinBackbox.value),
    }),
    classes: [common, danger],
  }
})

const isTogglePublicationStateDisabled = computed(() => {
  if (initialPrice.value === '' || initialPrice.value === '0') {
    return true
  }

  return !(parseInt(formattedPrice.value, 10) > 0)
})

const isSaveListingDisabled = computed(() => {
  const priceHasNotChanged =
    formattedPrice.value === props.listing.price?.amount
  const priceIsEmpty = formattedPrice.value === ''
  const priceIsNegative = parseInt(formattedPrice.value, 10) < 0

  return priceHasNotChanged || priceIsEmpty || priceIsNegative
})

const publicationStateCtaLabel = computed(() =>
  isListingOnline.value
    ? i18n(translations.turnOffline)
    : i18n(translations.turnOnline),
)

function savePrice() {
  const listingPostPayload: PostBuyBackQuoteListing = {
    grade: props.listing.grade,
    price: {
      amount: formattedPrice.value,
      currency: currency.value,
    },
  }

  emit('save-price', listingPostPayload)

  if (props.listing.listingId) {
    trackClick({
      name: 'tradein_save-backbox',
      zone: 'tradein_listing_price_update',
      page_type: 'tradein_listings',
      sourcing_listing_id: props.listing.listingId,
      sourcing_listing_title: props.title,
      sourcing_listing_backbox_grade: props.listing.grade,
      listing_market: {
        market: props.marketCode,
        currency: props.listing.price.currency,
        tradein_backbox_price: props.listing.backboxPrice?.amount ?? '',
        tradein_input_price: formattedPrice.value,
        tradein_price_to_win_backbox:
          props.listing.priceToWinBackbox?.amount ?? '',
      },
    })
  }
}

function winBackbox() {
  formattedPrice.value = priceToWinBackbox.value.amount
  const listingPostPayload: PostBuyBackQuoteListing = {
    grade: props.listing.grade,
    price: priceToWinBackbox.value,
  }

  emit('save-price', listingPostPayload)

  if (props.listing.listingId) {
    trackClick({
      name: 'tradein_win-backbox',
      zone: 'tradein_listing_price_update',
      page_type: 'tradein_listings',
      sourcing_listing_id: props.listing.listingId,
      sourcing_listing_title: props.title,
      sourcing_listing_backbox_grade: props.listing.grade,
      listing_market: {
        market: props.marketCode,
        currency: props.listing.price.currency,
        tradein_backbox_price: props.listing.backboxPrice?.amount ?? '',
        tradein_input_price: props.listing.priceToWinBackbox?.amount ?? '',
        tradein_price_to_win_backbox:
          props.listing.priceToWinBackbox?.amount ?? '',
      },
    })
  }
}

function toggleState() {
  // ABOUT PUTTING LISTING OFFLINE
  // Putting amount to 0 will automatically  put the listing offline and will not allow
  // to move to listing back online without changing the price.
  // This is the behavior we want for manual operations
  // (i.e., the refurbisher don't want to buy anymore the grade).
  // Changing only the state will put the listing offline but will leave the price,
  // allowing the listing to be put directly back online. This is the behavior we want
  // for batch operations (i.e., staff need to put all online listings of a refurbisher offline)

  const payload =
    props.listing.state === LISTING_PUBLICATION_STATE.ONLINE
      ? {
          grade: props.listing.grade,
          price: {
            amount: '0',
            currency: currency.value,
          },
        }
      : {
          grade: props.listing.grade,
          state: BuyBackQuoteListingStates.ENABLED,
        }

  emit('toggle-state', payload)
}
</script>
